import React, { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { FolderKanban, Plus, RefreshCcw, Edit, Trash2 } from 'lucide-react';
import ColumnTitle from '@/components/ui/DataTable/ColumnTitle';
import Button from '@/components/ui/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/components/ui/Button/ButtonSize';
import DataTable from '@/components/ui/DataTable';
import { Section } from '@/types/section.types';
import {
  useGetSectionsQuery,
} from '@/components/features/sections/services/section.queries';
import {
  useAddSectionMutation,
  useUpdateSectionMutation,
  useDeleteSectionMutation,
} from '@/components/features/sections/services/section.mutations';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/ui/Modal';
import useUserStore from '@/stores/useUserStore';

const SectionsList = () => {
  const navigate = useNavigate();
  const { data: sections = [], isLoading, refetch } = useGetSectionsQuery();
  const userStore = useUserStore(state => state.user);
  const isAdministrator = userStore.roles?.includes('administrator') || false;
  const addSectionMutation = useAddSectionMutation();
  const updateSectionMutation = useUpdateSectionMutation();
  const deleteSectionMutation = useDeleteSectionMutation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [sectionName, setSectionName] = useState('');

  const handleAddSection = async () => {
    await addSectionMutation.mutateAsync({ section_name: sectionName });
    setIsAddModalOpen(false);
    setSectionName('');
  };

  const handleEditSection = async () => {
    if (selectedSection) {
      await updateSectionMutation.mutateAsync({
        sectionId: selectedSection.section_id,
        section: { section_name: sectionName },
      });
      setIsEditModalOpen(false);
      setSelectedSection(null);
      setSectionName('');
    }
  };

  const handleDeleteSection = async () => {
    if (selectedSection) {
      await deleteSectionMutation.mutateAsync(selectedSection.section_id);
      setIsDeleteModalOpen(false);
      setSelectedSection(null);
    }
  };

  const openEditModal = (section: Section) => {
    setSelectedSection(section);
    setSectionName(section.section_name);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (section: Section) => {
    setSelectedSection(section);
    setIsDeleteModalOpen(true);
  };

  const columns: ColumnDef<Section, string>[] = [
    {
      id: 'section_name',
      header: ({ column }) => <ColumnTitle title="Όνομα Ενότητας" isSortable column={column} />,
      accessorKey: 'section_name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FolderKanban className="w-4 h-4 text-gray-400" />
          <span>{row.original.section_name}</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Ενέργειες',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant={ButtonVariant.GHOST}
            size={ButtonSize.SM}
            onClick={() => navigate(`/sections/${row.original.section_id}`)}
            type={ButtonType.BUTTON}
            title="Προβολή κατηγοριών"
          >
            Κατηγορίες
          </Button>
          {isAdministrator && (
            <>
              <Button
                variant={ButtonVariant.GHOST}
                size={ButtonSize.SM}
                onClick={() => openEditModal(row.original)}
                type={ButtonType.BUTTON}
              >
                <Edit className="w-4 h-4" />
              </Button>
              <Button
                variant={ButtonVariant.GHOST}
                size={ButtonSize.SM}
                onClick={() => openDeleteModal(row.original)}
                type={ButtonType.BUTTON}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (isLoading) return null;

  return (
    <div className="h-full w-full">
      <div className="mx-auto bg-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Ενότητες</h1>
          <p className="mt-1 text-sm text-gray-500">Διαχείριση Ενοτήτων Κατηγοριών</p>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={ButtonVariant.OUTLINE}
              size={ButtonSize.SM}
              className="flex items-center"
              onClick={() => refetch()}
              type={ButtonType.BUTTON}
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Ανανέωση
            </Button>
          </div>
          {isAdministrator && (
            <div className="flex gap-2">
              <Button
                onClick={() => setIsAddModalOpen(true)}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SM}
                className="flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Προσθήκη Ενότητας
              </Button>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow">
          <DataTable columns={columns} data={sections} enableColumnFilters={true} />
        </div>
      </div>

      {/* Add Section Modal */}
      <Modal
        open={isAddModalOpen}
        setOpen={setIsAddModalOpen}
        title="Προσθήκη Ενότητας"
        primaryAction={{
          text: addSectionMutation.isPending ? 'Προσθήκη...' : 'Προσθήκη',
          onClick: handleAddSection,
        }}
        secondaryAction={{
          text: 'Ακύρωση',
          onClick: () => {
            setIsAddModalOpen(false);
            setSectionName('');
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Όνομα Ενότητας
            </label>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="Εισάγετε το όνομα της ενότητας"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </Modal>

      {/* Edit Section Modal */}
      <Modal
        open={isEditModalOpen}
        setOpen={setIsEditModalOpen}
        title="Επεξεργασία Ενότητας"
        primaryAction={{
          text: updateSectionMutation.isPending ? 'Αποθήκευση...' : 'Αποθήκευση',
          onClick: handleEditSection,
        }}
        secondaryAction={{
          text: 'Ακύρωση',
          onClick: () => {
            setIsEditModalOpen(false);
            setSelectedSection(null);
            setSectionName('');
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Όνομα Ενότητας
            </label>
            <input
              type="text"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
              placeholder="Εισάγετε το όνομα της ενότητας"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </Modal>

      {/* Delete Section Modal */}
      <Modal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title="Διαγραφή Ενότητας"
        type="delete"
        primaryAction={{
          text: deleteSectionMutation.isPending ? 'Διαγραφή...' : 'Διαγραφή',
          onClick: handleDeleteSection,
        }}
        secondaryAction={{
          text: 'Ακύρωση',
          onClick: () => {
            setIsDeleteModalOpen(false);
            setSelectedSection(null);
          },
        }}
      >
        <p className="text-sm text-gray-600">
          Είστε σίγουροι ότι θέλετε να διαγράψετε την ενότητα "{selectedSection?.section_name}";
        </p>
      </Modal>
    </div>
  );
};

export default SectionsList;
